import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VoiceButton from "@/components/VoiceButton";

const PLATFORM_STYLE = {
  facebook: "bg-[#1877F2]",
  instagram: "bg-[#E4405F]",
  tiktok: "bg-[#0F2547]",
  linkedin: "bg-[#0A66C2]",
};
const STATUS_STYLE = {
  idea: ["Idea", "bg-slate-100 text-slate-600"],
  scheduled: ["Scheduled", "bg-sky-50 text-[#005EB8]"],
  posted: ["Posted", "bg-emerald-50 text-[#007F3B]"],
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const shareLinks = (text) => {
  const t = encodeURIComponent(text || "");
  const u = encodeURIComponent(window.location.origin);
  return [
    ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`, "post-share-facebook"],
    ["X", `https://twitter.com/intent/tweet?text=${t}`, "post-share-x"],
    ["WhatsApp", `https://wa.me/?text=${t}`, "post-share-whatsapp"],
    ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, "post-share-linkedin"],
  ];
};

const PostPreview = ({ text, platforms, date, time, imgUrl }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="post-preview">
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="h-8 w-8 rounded-full bg-[#0F2547] text-white grid place-items-center text-xs font-black shrink-0">W</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-[#0F2547]">Wales Event Medical Services</p>
        <p className="text-[10px] text-slate-400">
          {date}{time ? ` · ${time}` : ""} · {platforms.length ? platforms.join(", ") : "no platform tagged"}
        </p>
      </div>
    </div>
    <p className="px-3 pb-2 text-sm text-slate-800 whitespace-pre-wrap">{text || "Your post text…"}</p>
    {imgUrl && <img src={imgUrl} alt="" className="w-full max-h-48 object-cover border-t border-slate-100" />}
    <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-400 flex gap-4">
      <span>Like</span><span>Comment</span><span>Share</span>
    </div>
  </div>
);

export default function SocialCalendar() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [posts, setPosts] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ text: "", platforms: [], status: "idea", date: "", time: "" });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/social-posts", { params: { month: monthKey } });
      setPosts(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => {
    const first = month;
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [month]);

  const postsByDay = useMemo(() => {
    const map = {};
    (posts || []).forEach((p) => { (map[p.date] = map[p.date] || []).push(p); });
    return map;
  }, [posts]);

  const openAdd = (d) => {
    setForm({ text: "", platforms: ["facebook"], status: "idea", date: dayKey(d), time: "09:00" });
    setImage(null);
    setDialog({ mode: "add" });
  };

  const openEdit = (p) => {
    setForm({ text: p.text, platforms: p.platforms, status: p.status, date: p.date, time: p.time || "" });
    setImage(null);
    setDialog({ mode: "edit", post: p });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        const fd = new FormData();
        fd.append("date", form.date);
        fd.append("text", form.text);
        fd.append("platforms", JSON.stringify(form.platforms));
        fd.append("status", form.status);
        fd.append("time", form.time);
        if (image) fd.append("image", image);
        await api.post("/social-posts", fd);
        toast.success("Post added to the calendar");
      } else {
        await api.patch(`/social-posts/${dialog.post.id}`, {
          date: form.date, time: form.time, text: form.text, platforms: form.platforms, status: form.status,
        });
        toast.success("Post updated");
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/social-posts/${dialog.post.id}`);
      toast.success("Post deleted");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePlatform = (p) =>
    setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }));

  const todayKey = dayKey(new Date());
  const monthLabel = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-4" data-testid="social-calendar">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} data-testid="cal-prev"
          className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 hover:border-[#005EB8] transition-colors">
          <ChevronLeft className="h-4 w-4 text-[#0F2547]" />
        </button>
        <h2 className="font-heading text-base font-bold text-[#0F2547] flex-1 text-center" data-testid="cal-month-label">{monthLabel}</h2>
        <button onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today"
          className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600 hover:border-[#005EB8] transition-colors">
          Today
        </button>
        <button onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} data-testid="cal-next"
          className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 hover:border-[#005EB8] transition-colors">
          <ChevronRight className="h-4 w-4 text-[#0F2547]" />
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mb-2">Tap a day to plan a post — add a picture or logo, tag the platforms, and track it idea → scheduled → posted.</p>

      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200">
        {DOW.map((d) => (
          <div key={d} className="bg-[#F4F7FA] text-[10px] font-bold uppercase tracking-wider text-slate-500 text-center py-1.5">{d}</div>
        ))}
        {cells.map((d) => {
          const key = dayKey(d);
          const dayPosts = postsByDay[key] || [];
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <div key={key} onClick={() => openAdd(d)} data-testid={`cal-day-${key}`}
              className={`bg-white min-h-[76px] sm:min-h-[96px] p-1 cursor-pointer hover:bg-sky-50/50 transition-colors ${inMonth ? "" : "opacity-40"}`}>
              <p className={`text-[11px] font-bold mb-0.5 h-5 w-5 grid place-items-center rounded-full ${key === todayKey ? "bg-[#005EB8] text-white" : "text-slate-500"}`}>
                {d.getDate()}
              </p>
              <div className="space-y-0.5">
                {dayPosts.slice(0, 2).map((p) => (
                  <button key={p.id} onClick={(e) => { e.stopPropagation(); openEdit(p); }} data-testid={`post-chip-${p.id}`}
                    className="w-full flex items-center gap-1 rounded px-1 py-0.5 bg-[#F4F7FA] border border-slate-200 hover:border-[#005EB8] text-left transition-colors">
                    {p.has_image && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/social-posts/${p.id}/image`} alt=""
                        className="h-4 w-4 rounded object-cover shrink-0" />
                    )}
                    <span className="text-[10px] font-semibold text-slate-700 truncate flex-1">{p.text}</span>
                    {p.time && <span className="text-[9px] font-bold text-slate-400 shrink-0">{p.time}</span>}
                    <span className="flex gap-0.5 shrink-0">
                      {p.platforms.map((pl) => <span key={pl} className={`h-1.5 w-1.5 rounded-full ${PLATFORM_STYLE[pl]}`} />)}
                    </span>
                  </button>
                ))}
                {dayPosts.length > 2 && <p className="text-[9px] text-slate-400 font-semibold pl-1">+{dayPosts.length - 2} more</p>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">
              {dialog?.mode === "edit" ? "Edit post" : "Plan a post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="post-form">
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="post-date" title="Date it goes out" className={`${inputCls} bg-white`} />
              <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                data-testid="post-time" title="Time it goes out" className={`${inputCls} bg-white`} />
            </div>
            <p className="text-[10px] text-slate-400 -mt-1">The date &amp; time you want it sent out — check the preview below before anything goes online.</p>
            <div className="flex items-start gap-2">
              <textarea rows={4} required value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} data-testid="post-text"
                placeholder="What's the post? Type or tap the mic and talk…"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
              <div className="pt-1.5">
                <VoiceButton testid="post-voice" onTranscript={(t) => setForm((f) => ({ ...f, text: (f.text ? f.text + " " : "") + t }))} />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Platform tags</p>
              <div className="flex gap-2 flex-wrap">
                {Object.keys(PLATFORM_STYLE).map((p) => (
                  <button type="button" key={p} onClick={() => togglePlatform(p)} data-testid={`post-platform-${p}`}
                    className={`h-9 px-3 rounded-lg text-xs font-bold capitalize transition-colors ${form.platforms.includes(p) ? `${PLATFORM_STYLE[p]} text-white` : "border border-slate-300 text-slate-500 hover:border-slate-400"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Status</p>
              <div className="flex gap-2">
                {Object.entries(STATUS_STYLE).map(([k, [label]]) => (
                  <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, status: k }))} data-testid={`post-status-${k}`}
                    className={`h-9 px-3 rounded-lg text-xs font-bold transition-colors ${form.status === k ? "bg-[#0F2547] text-white" : "border border-slate-300 text-slate-500 hover:border-slate-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {dialog?.mode === "add" ? (
              <label className="flex items-center gap-2 h-12 px-3 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 cursor-pointer hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <Paperclip className="h-4 w-4 shrink-0" />
                <span className="truncate">{image ? image.name : "Add a picture or logo (optional)"}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="post-image"
                  onChange={(e) => setImage(e.target.files?.[0] || null)} />
              </label>
            ) : dialog?.post?.has_image && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/social-posts/${dialog.post.id}/image`} alt="Post"
                className="max-h-40 rounded-lg border border-slate-200 object-contain" data-testid="post-image-preview" />
            )}
            <div>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Preview — how it'll look online</p>
              <PostPreview text={form.text} platforms={form.platforms} date={form.date} time={form.time}
                imgUrl={image ? URL.createObjectURL(image) : dialog?.post?.has_image ? `${process.env.REACT_APP_BACKEND_URL}/api/social-posts/${dialog.post.id}/image` : null} />
            </div>
            {dialog?.mode === "edit" && (
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Send it out now — opens with the text ready</p>
                <div className="flex gap-2 flex-wrap" data-testid="post-share-row">
                  {shareLinks(form.text).map(([label, href, testid]) => (
                    <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={testid}
                      className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] inline-flex items-center transition-colors">
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button type="submit" disabled={saving || !form.text.trim()} data-testid="post-save"
                className="flex-1 h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-bold">
                {saving ? "Saving…" : dialog?.mode === "edit" ? "Save changes" : "Add to calendar"}
              </button>
              {dialog?.mode === "edit" && (
                <button type="button" onClick={remove} data-testid="post-delete"
                  className="h-12 w-12 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#D5281B] hover:border-[#D5281B] transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
